import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';

interface PinnedItem {
  id: string;
  title: string;
  link: string;
  icon?: string;
  section: string;
  sectionTitle: string;
  groupTitle?: string;
  timestamp: number;
}

@Module({
  dynamic: true,
  store,
  name: 'pinnedItems',
  namespaced: true
})
class PinnedItemsModule extends VuexModule {
  private pinnedItems: PinnedItem[] = [];
  private maxPinnedItems: number = 10;
  
  get getPinnedItems(): PinnedItem[] {
    return this.pinnedItems;
  }
  
  get isPinned(): (itemId: string) => boolean {
    return (itemId: string) => {
      return this.pinnedItems.some(item => item.id === itemId);
    };
  }
  
  get pinnedCount(): number {
    return this.pinnedItems.length;
  }
  
  @Mutation
  SET_PINNED_ITEMS(items: PinnedItem[]): void {
    this.pinnedItems = items;
  }
  
  @Mutation
  ADD_PINNED_ITEM(item: PinnedItem): void {
    // Remove if already exists (move to top)
    this.pinnedItems = this.pinnedItems.filter(p => p.id !== item.id);
    
    // Add to beginning
    this.pinnedItems.unshift(item);
    
    // Limit max items - remove oldest
    if (this.pinnedItems.length > this.maxPinnedItems) {
      this.pinnedItems = this.pinnedItems.slice(0, this.maxPinnedItems);
    }
  }
  
  @Mutation
  REMOVE_PINNED_ITEM(itemId: string): void {
    this.pinnedItems = this.pinnedItems.filter(item => item.id !== itemId);
  }
  
  @Action
  async loadPinnedItems(storeId: number): Promise<void> {
    try {
      const storageKey = `pinned_items_${storeId}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const items = JSON.parse(saved);
        // Validate data structure
        if (Array.isArray(items)) {
          this.SET_PINNED_ITEMS(items);
        }
      }
    } catch (error) {
      console.warn('Failed to load pinned items:', error);
      this.SET_PINNED_ITEMS([]);
    }
  }
  
  @Action
  async savePinnedItems(storeId: number): Promise<void> {
    try {
      const storageKey = `pinned_items_${storeId}`;
      localStorage.setItem(storageKey, JSON.stringify(this.pinnedItems));
    } catch (error) {
      console.error('Failed to save pinned items:', error);
    }
  }
  
  @Action
  async togglePinItem(payload: {
    item: {
      title: string;
      link: string;
      icon?: string;
      section: string;
      sectionTitle: string;
      groupTitle?: string;
    };
    storeId: number;
  }): Promise<boolean> {
    const { item, storeId } = payload;
    
    // Generate ID inline since private methods aren't accessible in actions
    const sanitize = (str: string) => str
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const parts = [item.section];
    if (item.groupTitle) {
      parts.push(sanitize(item.groupTitle));
    }
    parts.push(sanitize(item.title));
    const itemId = parts.join('-');
    
    // Check if already pinned using getter
    const isPinned = this.context.getters.isPinned(itemId);
    
    if (isPinned) {
      this.context.commit('REMOVE_PINNED_ITEM', itemId);
      await this.context.dispatch('savePinnedItems', storeId);
      return false; // unpinned
    } else {
      const pinnedItem: PinnedItem = {
        ...item,
        id: itemId,
        timestamp: Date.now()
      };
      this.context.commit('ADD_PINNED_ITEM', pinnedItem);
      await this.context.dispatch('savePinnedItems', storeId);
      return true; // pinned
    }
  }
  
  @Action
  async unpinItem(payload: { itemId: string; storeId: number }): Promise<void> {
    this.context.commit('REMOVE_PINNED_ITEM', payload.itemId);
    await this.context.dispatch('savePinnedItems', payload.storeId);
  }
  
  @Action
  clearPinnedItems(): void {
    this.context.commit('SET_PINNED_ITEMS', []);
  }
}

export default getModule(PinnedItemsModule, store);